import BlockIcon from '@mui/icons-material/Block';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import HistoryIcon from '@mui/icons-material/History';
import DoNotDisturbIcon from '@mui/icons-material/DoNotDisturb';
import GroupIcon from '@mui/icons-material/Group';
import GroupAddIcon from '@mui/icons-material/GroupAdd';
import GroupsIcon from '@mui/icons-material/Groups';
import LinkedInIcon from '@mui/icons-material/LinkedIn';
import MarkEmailReadIcon from '@mui/icons-material/MarkEmailRead';
import MoreIcon from '@mui/icons-material/More';
import RadioButtonCheckedIcon from '@mui/icons-material/RadioButtonChecked';
import ScreenSearchDesktopIcon from '@mui/icons-material/ScreenSearchDesktop';
import VerifiedIcon from '@mui/icons-material/Verified';
import WcIcon from '@mui/icons-material/Wc';
import WorkspacePremiumIcon from '@mui/icons-material/WorkspacePremium';
import { Stack, Typography } from '@mui/material';
import { LinkedinTypes, ProductGroups } from 'enums';
import { RecruiterLocationSearch, RegularConstraint } from 'features/co-pilot/co-pilot.types';
import getLocalizedText from 'features/co-pilot/translationFileProduct';
import _ from 'lodash';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { makeStyles } from 'tss-react/mui';
import {
  CampaignBuilderSteps,
  CampaignPipelineSteps,
  ContactFilter,
  ContactSearchMethods,
  ContactToAdd,
  SearchObjective,
  SearchToAdd,
  SelectorIds,
} from '../campaign-builder.types';

const MainStep: React.FC<{ active: boolean; icon: React.ReactNode; text: string; onClick(): void }> = ({ active, icon, text, onClick }) => {
  const { classes, cx } = useStyles();

  return (
    <Stack className={cx(classes.mainStep, active ? 'active' : undefined)} direction={'row'} alignItems={'center'} gap={1} onClick={onClick}>
      {icon}
      {text}
    </Stack>
  );
};

const SubStep: React.FC<{ isActive?: boolean; icon?: React.ReactElement; primaryText: string; secondaryText?: string; onClick(): void }> = ({
  isActive,
  icon,
  primaryText,
  secondaryText,
  onClick,
}) => {
  const { classes, cx } = useStyles();

  return (
    <Stack className={classes.subStep} direction={'row'} alignItems={'center'} justifyContent={'space-between'} onClick={onClick}>
      <Stack direction={'row'} alignItems={'center'} gap={1}>
        {icon ? icon : <CheckCircleIcon />}
        <Typography className={cx(classes.subStepText, isActive ? classes.highlight : undefined)}>
          <strong>{primaryText}</strong>
          {secondaryText ? ':' : ''} {secondaryText}
        </Typography>
      </Stack>
    </Stack>
  );
};

interface Props {
  productGroup: ProductGroups;
  activeCampaignStep: CampaignBuilderSteps;

  activeCampaignPipelineStep: CampaignPipelineSteps;
  contactSearchMethod: ContactSearchMethods | '';
  linkedinProduct: LinkedinTypes | '';
  paiSearchLanguage: string[];
  regularLocationConstraints: RegularConstraint;
  recruiterLocationConstraints: RecruiterLocationSearch;
  selectedContactSearchObjective: SearchObjective | null;
  searchToAdd: SearchToAdd | null;
  separateLinkedinAccount: string;
  contactsToAdd: ContactToAdd[];

  contactFilter: ContactFilter;

  handleActiveCampaignPipelineStepChange(newStep: CampaignPipelineSteps): () => void;

  applySidebarFilter(filter: ContactFilter): () => void;
}

const StepsSidebar: React.FC<Props> = ({
  productGroup,
  activeCampaignStep,
  activeCampaignPipelineStep,
  contactSearchMethod,
  linkedinProduct,
  paiSearchLanguage,
  regularLocationConstraints,
  recruiterLocationConstraints,
  selectedContactSearchObjective,
  searchToAdd,
  separateLinkedinAccount,
  contactsToAdd,
  contactFilter,
  handleActiveCampaignPipelineStepChange,
  applySidebarFilter,
}) => {
  const { classes } = useStyles();
  const { t } = useTranslation();

  // The KI search is the default view and fills method, account, product and language
  // itself — listing them as sub-steps would show choices the user never made.
  const isKiSearch = contactSearchMethod === ContactSearchMethods.WE_SEARCH;
  const isSearchMethodSelected = !!contactSearchMethod && !isKiSearch;
  const isSearchObjectiveSelected = !_.isNil(selectedContactSearchObjective);
  const isValidUrl = !_.isNil(searchToAdd) && (searchToAdd.search.url === '' || new RegExp(searchToAdd.regex).test(searchToAdd.search.url));
  const isSeparateLinkedinAccountSelected = !!separateLinkedinAccount && !isKiSearch;
  const areContactsUploaded = !_.isEmpty(contactsToAdd);
  const isLinkedinProductSelected = !!linkedinProduct && !isKiSearch;
  const areSearchLanguagesSelected = isSearchMethodSelected && isLinkedinProductSelected && !_.isEmpty(paiSearchLanguage);
  const areRegularLocationsSelected = !_.isEmpty(regularLocationConstraints.selected);
  const areRecruiterLocationsSelected =
    !_.isEmpty(recruiterLocationConstraints.geo.selected) || !_.isEmpty(recruiterLocationConstraints.zip.selected);

  const regularLocations = regularLocationConstraints.selected.map((location) => location[1]).join(', ');
  const recruiterLocations = !_.isEmpty(recruiterLocationConstraints.geo.selected)
    ? recruiterLocationConstraints.geo.selected.map((location) => location.label)?.join(', ')
    : recruiterLocationConstraints.zip.selected.map((location) => location.label)?.join(', ');

  const scrollToSection = (section: SelectorIds) => {
    return () => {
      const scrollContainer = document.getElementById(SelectorIds.STEP_CONTENT);
      if (!_.isNil(scrollContainer)) {
        const targetSection = document.getElementById(section);

        if (!_.isNil(targetSection)) {
          const scrollOffset = targetSection.offsetTop - scrollContainer.offsetTop;

          scrollContainer.scrollTo({
            top: scrollOffset,
            behavior: 'smooth',
          });
        }
      }
    };
  };

  const getSearchMethodText = () => {
    switch (contactSearchMethod) {
      case ContactSearchMethods.WE_SEARCH:
        return t('paiSearch');
      case ContactSearchMethods.OWN_SEARCH:
        return t('conductOwnSearch');
      case ContactSearchMethods.UPLOAD_CONTACT_LIST:
        return t('existingContactList');
      case ContactSearchMethods.ADD_FROM_CAMPAIGN:
        return t('addContactsFromCampaign');
      default:
        return '';
    }
  };

  const mapLiProductType = (product: LinkedinTypes | undefined) => {
    switch (product) {
      case LinkedinTypes.LINKEDIN:
        return 'LinkedIn';

      case LinkedinTypes.SALES_NAV:
        return 'Sales Navigator';

      case LinkedinTypes.RECRUITER:
        return 'Recruiter';

      default:
        return 'LinkedIn';
    }
  };

  const getSidebarContent = () => {
    switch (activeCampaignStep) {
      case CampaignBuilderSteps.CAMPAIGN_BRIEFING:
        return (
          <>
            <Typography className={classes.stepDescription}>{getLocalizedText('campaignBriefingDescription', productGroup, t)}</Typography>
          </>
        );
      case CampaignBuilderSteps.CAMPAIGN_CONTACT_LIST:
        return (
          <>
            <Typography className={classes.stepDescription}>{getLocalizedText('campaignContactListDescription', productGroup, t)}</Typography>
            <Stack>
              <MainStep
                active={activeCampaignPipelineStep === CampaignPipelineSteps.ADD_CONTACTS}
                icon={<GroupAddIcon />}
                text={getLocalizedText('addContacts', productGroup, t)}
                onClick={handleActiveCampaignPipelineStepChange(CampaignPipelineSteps.ADD_CONTACTS)}
              />
              {activeCampaignPipelineStep === CampaignPipelineSteps.ADD_CONTACTS ? (
                <>
                  {isSearchMethodSelected ? (
                    <SubStep
                      primaryText={t('searchMethod')}
                      secondaryText={getSearchMethodText()}
                      onClick={scrollToSection(SelectorIds.SEARCH_METHOD)}
                    />
                  ) : null}
                  {isSeparateLinkedinAccountSelected ? (
                    <SubStep
                      primaryText={t('crawlingAccount')}
                      secondaryText={_.truncate(separateLinkedinAccount, { length: 20 })}
                      onClick={scrollToSection(SelectorIds.CRAWLING_ACCOUNT)}
                    />
                  ) : null}
                  {isLinkedinProductSelected ? (
                    <SubStep
                      primaryText={t('productToBeUsed')}
                      secondaryText={mapLiProductType(linkedinProduct)}
                      onClick={scrollToSection(SelectorIds.LINKEDIN_PRODUCT)}
                    />
                  ) : null}
                  {isSearchObjectiveSelected ? (
                    <SubStep
                      primaryText={getLocalizedText('searchObjective', productGroup, t)}
                      secondaryText={selectedContactSearchObjective.title || ''}
                      onClick={scrollToSection(SelectorIds.OWN_SEARCH_OBJECTIVE)}
                    />
                  ) : null}
                  {isValidUrl ? (
                    <SubStep
                      primaryText={t('pastedUrls')}
                      secondaryText={t('urlValid')}
                      onClick={scrollToSection(SelectorIds.OWN_SEARCH_OBJECTIVE)}
                    />
                  ) : null}
                  {areContactsUploaded ? (
                    <SubStep
                      primaryText={t('uploadedContacts')}
                      secondaryText={t('leadsCount', { count: contactsToAdd.length })}
                      onClick={scrollToSection(SelectorIds.CONTACT_LIST)}
                    />
                  ) : null}
                  {areSearchLanguagesSelected ? (
                    <SubStep
                      primaryText={t('languages')}
                      secondaryText={paiSearchLanguage.map((lang) => t(lang)).join(', ')}
                      onClick={scrollToSection(SelectorIds.PAI_SEARCH_LANGUAGE)}
                    />
                  ) : null}
                  {areRegularLocationsSelected ? (
                    <SubStep
                      primaryText={t('searchLocation')}
                      secondaryText={_.truncate(regularLocations, { length: 20 })}
                      onClick={scrollToSection(SelectorIds.SEARCH_LOCATION)}
                    />
                  ) : null}
                  {areRecruiterLocationsSelected ? (
                    <SubStep
                      primaryText={t('searchLocation')}
                      secondaryText={_.truncate(recruiterLocations, { length: 20 })}
                      onClick={scrollToSection(SelectorIds.SEARCH_LOCATION)}
                    />
                  ) : null}
                </>
              ) : null}
              <MainStep
                active={activeCampaignPipelineStep === CampaignPipelineSteps.AI_CONTACT_QUALIFICATION}
                icon={<WorkspacePremiumIcon />}
                text={getLocalizedText('aiContactQualification', productGroup, t)}
                onClick={handleActiveCampaignPipelineStepChange(CampaignPipelineSteps.AI_CONTACT_QUALIFICATION)}
              />
              <MainStep
                active={activeCampaignPipelineStep === CampaignPipelineSteps.MANAGE_SEARCHES}
                icon={<ScreenSearchDesktopIcon />}
                text={t('manageSearches')}
                onClick={handleActiveCampaignPipelineStepChange(CampaignPipelineSteps.MANAGE_SEARCHES)}
              />
              <MainStep
                active={activeCampaignPipelineStep === CampaignPipelineSteps.MANAGE_BLACKLISTS}
                icon={<BlockIcon />}
                text={t('manageBlacklists')}
                onClick={handleActiveCampaignPipelineStepChange(CampaignPipelineSteps.MANAGE_BLACKLISTS)}
              />
              <MainStep
                active={activeCampaignPipelineStep === CampaignPipelineSteps.CONTACT_OVERVIEW}
                icon={<GroupIcon />}
                text={getLocalizedText('contactOverview', productGroup, t)}
                onClick={handleActiveCampaignPipelineStepChange(CampaignPipelineSteps.CONTACT_OVERVIEW)}
              />
              {activeCampaignPipelineStep === CampaignPipelineSteps.CONTACT_OVERVIEW ? (
                <>
                  <SubStep
                    isActive={contactFilter === ContactFilter.ALL}
                    icon={<GroupsIcon />}
                    primaryText={getLocalizedText('allContacts', productGroup, t)}
                    onClick={applySidebarFilter(ContactFilter.ALL)}
                  />
                  <SubStep
                    isActive={contactFilter === ContactFilter.ALREADY_CONTACTED}
                    icon={<MoreIcon sx={{ transform: 'rotate(180deg)' }} />}
                    primaryText={getLocalizedText('contactsInOtherCampaigns', productGroup, t)}
                    onClick={applySidebarFilter(ContactFilter.ALREADY_CONTACTED)}
                  />
                  <SubStep
                    isActive={contactFilter === ContactFilter.UNCERTAIN_SEX}
                    icon={<WcIcon />}
                    primaryText={getLocalizedText('uncertainSexContacts', productGroup, t)}
                    onClick={applySidebarFilter(ContactFilter.UNCERTAIN_SEX)}
                  />
                  <SubStep
                    isActive={contactFilter === ContactFilter.BLACKLISTED}
                    icon={<DoNotDisturbIcon />}
                    primaryText={getLocalizedText('blacklistedContacts', productGroup, t)}
                    onClick={applySidebarFilter(ContactFilter.BLACKLISTED)}
                  />
                </>
              ) : null}
              <MainStep
                active={activeCampaignPipelineStep === CampaignPipelineSteps.FINAL_CONTACT_LIST}
                icon={<VerifiedIcon />}
                text={getLocalizedText('finalContactList', productGroup, t)}
                onClick={handleActiveCampaignPipelineStepChange(CampaignPipelineSteps.FINAL_CONTACT_LIST)}
              />
            </Stack>
          </>
        );
      case CampaignBuilderSteps.CAMPAIGN_SETTINGS:
        return (
          <>
            <Typography className={classes.stepDescription}>{getLocalizedText('campaignSettingsDescription', productGroup, t)}</Typography>
            <Stack>
              <SubStep
                icon={<MarkEmailReadIcon />}
                primaryText={getLocalizedText('approveMessages', productGroup, t)}
                onClick={scrollToSection(SelectorIds.SETTINGS_APPROVE_MESSAGES)}
              />
              <SubStep
                icon={<LinkedInIcon />}
                primaryText={getLocalizedText('linkedinAccount', productGroup, t)}
                onClick={scrollToSection(SelectorIds.SETTINGS_LINKEDIN_ACCOUNT)}
              />
              <SubStep
                icon={<HistoryIcon />}
                primaryText={t('recontactCooldownSidebar')}
                onClick={scrollToSection(SelectorIds.SETTINGS_RECONTACT_COOLDOWN)}
              />
            </Stack>
          </>
        );
      case CampaignBuilderSteps.CAMPAIGN_SUMMARY:
        return (
          <>
            <Typography className={classes.stepDescription}>{getLocalizedText('campaignOverviewDescription', productGroup, t)}</Typography>
          </>
        );
      default:
        return <></>;
    }
  };

  return (
    <Stack className={classes.stepsSidebar}>
      <Stack className={classes.sidebarContent} gap={2}>
        <Stack direction={'row'} alignItems={'center'} gap={2}>
          <RadioButtonCheckedIcon className={classes.stepIcon} />
          <Typography className={classes.stepText}>{t(activeCampaignStep)}</Typography>
        </Stack>
        {getSidebarContent()}
      </Stack>
    </Stack>
  );
};

const useStyles = makeStyles()((theme) => ({
  stepsSidebar: {
    width: '350px',
    flexShrink: 0,
    height: '100%',
    overflow: 'auto',
    borderRight: `1px solid ${theme.palette.greys.lighter}`,
  },
  sidebarContent: {
    padding: '24px',
  },
  stepIcon: {
    fontSize: theme.typography.pxToRem(24),
    color: theme.palette.primary.main,
  },
  stepText: {
    fontSize: theme.typography.pxToRem(20),
    fontWeight: 'bold',
  },
  stepDescription: {
    fontSize: theme.typography.pxToRem(13),
    color: theme.palette.greys.medium,
  },
  mainStep: {
    cursor: 'pointer',
    color: theme.palette.greys.medium,
    padding: '8px 16px',
    borderRadius: '5px',
    '& .MuiTypography-root': {
      fontSize: theme.typography.pxToRem(13),
      color: theme.palette.greys.medium,
    },
    '&:hover': {
      background: theme.palette.backgrounds.main_new,
    },
    '&.active': {
      background: theme.palette.backgrounds.main_new,
      color: theme.palette.primary.main,
    },
  },
  subStep: {
    cursor: 'pointer',
    color: theme.palette.greys.medium,
    padding: '8px 16px 8px 24px',
    '& svg': {
      fontSize: theme.typography.pxToRem(20),
      color: theme.palette.primary.main,
      '&.edit-icon': {
        fontSize: theme.typography.pxToRem(16),
        color: theme.palette.greys.medium,
      },
    },
    '&:hover': {
      background: theme.palette.backgrounds.main_new,
    },
  },
  subStepText: {
    fontSize: theme.typography.pxToRem(13),
    color: theme.palette.greys.medium,
  },
  highlight: {
    color: theme.palette.primary.main,
  },
}));

export default StepsSidebar;
